/**
 * Client API du parcours procédural (L1–L5).
 * Réutilise l'instance axios partagée (`apiClient`) — intercepteur JWT Supabase
 * + classification d'erreurs déjà câblés.
 */
import { apiClient } from "@/lib/api";
import type {
  CalendrierComplet,
  CaseListItem,
  CcjaPrecedentLight,
  CompositionTribunal,
  DossierProcedure,
  IncidentProcedural,
  MesureProvisoire,
  PartiesResponse,
  RecevabiliteChecklist,
  RecevabiliteDecision,
  RedfernLine,
  TourType,
  TypeIncident,
  TypeMesure,
} from "@/types/procedure";

type CaseId = string | number;

// --- Dossier (détail + liste) ---
export const getDossier = (caseId: CaseId) =>
  apiClient.get<DossierProcedure>(`/api/v1/cases/${caseId}`);

export const listCases = () => apiClient.get<CaseListItem[]>(`/api/v1/cases`);

// --- L2 : Recevabilité ---
export const priseEnCharge = (caseId: CaseId) =>
  apiClient.post(`/api/v1/cases/${caseId}/recevabilite/prendre-en-charge`);

export const deciderRecevabilite = (
  caseId: CaseId,
  data: { decision: string; motif?: string; checklist: RecevabiliteChecklist },
) => apiClient.post(`/api/v1/cases/${caseId}/recevabilite/decision`, data);

export const getRecevabilite = (caseId: CaseId) =>
  apiClient.get<RecevabiliteDecision>(`/api/v1/cases/${caseId}/recevabilite`);

// --- L1 : Tribunal ---
export const getCompositionTribunal = (caseId: CaseId) =>
  apiClient.get<CompositionTribunal>(
    `/api/v1/cases/${caseId}/tribunal/membres`,
  );

export const ajouterMembreTribunal = (
  caseId: CaseId,
  data: { user_id: string; member_role: string },
) => apiClient.post(`/api/v1/cases/${caseId}/tribunal/membres`, data);

export const retirerMembreTribunal = (caseId: CaseId, userId: string) =>
  apiClient.delete(`/api/v1/cases/${caseId}/tribunal/membres/${userId}`);

export const constituerTribunal = (caseId: CaseId) =>
  apiClient.post(`/api/v1/cases/${caseId}/tribunal/constituer`);

// --- L3 : Calendrier ---
export const getCalendrier = (caseId: CaseId) =>
  apiClient.get<CalendrierComplet>(`/api/v1/cases/${caseId}/calendrier`);

export const creerTourCalendrier = (
  caseId: CaseId,
  data: { tour_type: TourType; partie_cible: string; deadline: string },
) => apiClient.post(`/api/v1/cases/${caseId}/calendrier/tours`, data);

export const cloturerTourCalendrier = (caseId: CaseId, roundId: string) =>
  apiClient.post(
    `/api/v1/cases/${caseId}/calendrier/tours/${roundId}/cloturer`,
  );

// --- Enrôlement des parties & conseils (greffe) ---
export const getParties = (caseId: CaseId) =>
  apiClient.get<PartiesResponse>(`/api/v1/cases/${caseId}/parties`);

export const enrolerPartie = (
  caseId: CaseId,
  data: { user_id: string; member_role: string },
) => apiClient.post(`/api/v1/cases/${caseId}/parties/membres`, data);

export const radierPartie = (caseId: CaseId, userId: string) =>
  apiClient.delete(`/api/v1/cases/${caseId}/parties/membres/${userId}`);

// --- L9 : Production de documents (Redfern Schedule) ---
export const getProduction = (caseId: CaseId) =>
  apiClient.get<RedfernLine[]>(`/api/v1/cases/${caseId}/production`);

export const creerRequeteProduction = (
  caseId: CaseId,
  data: {
    partie_requise: string;
    documents_demandes: string;
    pertinence: string;
  },
) => apiClient.post(`/api/v1/cases/${caseId}/production`, data);

export const objecterProduction = (
  caseId: CaseId,
  requestId: string,
  objection: string,
) =>
  apiClient.post(`/api/v1/cases/${caseId}/production/${requestId}/objection`, {
    objection,
  });

export const deciderProduction = (
  caseId: CaseId,
  requestId: string,
  data: { decision: string; motif?: string; delai_production?: string },
) =>
  apiClient.post(`/api/v1/cases/${caseId}/production/${requestId}/decision`, data);

// --- L4 : Débats ---
export const cloturerDebats = (caseId: CaseId) =>
  apiClient.post(`/api/v1/cases/${caseId}/debats/cloturer`);

export const rouvrirDebats = (caseId: CaseId, motif: string) =>
  apiClient.post(`/api/v1/cases/${caseId}/debats/rouvrir`, { motif });

// --- L7 : Mesures provisoires & conservatoires (AUA art. 13) ---
export const getMesures = (caseId: CaseId) =>
  apiClient.get<MesureProvisoire[]>(
    `/api/v1/cases/${caseId}/mesures-provisoires`,
  );

export const demanderMesure = (
  caseId: CaseId,
  data: { type_mesure: TypeMesure; objet: string; urgence: boolean },
) =>
  apiClient.post<MesureProvisoire>(
    `/api/v1/cases/${caseId}/mesures-provisoires`,
    data,
  );

export const deciderMesure = (
  caseId: CaseId,
  mesureId: string,
  data: { decision: string; motif?: string },
) =>
  apiClient.post<MesureProvisoire>(
    `/api/v1/cases/${caseId}/mesures-provisoires/${mesureId}/decision`,
    data,
  );

export const leverMesure = (caseId: CaseId, mesureId: string) =>
  apiClient.post<MesureProvisoire>(
    `/api/v1/cases/${caseId}/mesures-provisoires/${mesureId}/lever`,
  );

// --- L8 : Procédures incidentes (récusation / exception d'incompétence) ---
export const getIncidents = (caseId: CaseId) =>
  apiClient.get<IncidentProcedural[]>(`/api/v1/cases/${caseId}/incidents`);

export const souleverIncident = (
  caseId: CaseId,
  data: {
    incident_type: TypeIncident;
    motif: string;
    arbitre_cible?: string;
    vise_tout_le_tribunal?: boolean;
    fondement_textuel?: string;
  },
) =>
  apiClient.post<IncidentProcedural>(`/api/v1/cases/${caseId}/incidents`, data);

export const deciderIncident = (
  caseId: CaseId,
  incidentId: string,
  data: { decision: string; motif?: string },
) =>
  apiClient.post<IncidentProcedural>(
    `/api/v1/cases/${caseId}/incidents/${incidentId}/decision`,
    data,
  );

// Dictionnaire léger des précédents CCJA (hydrate la suggestion jurisprudentielle).
export const getCcjaPrecedents = (typeIncident?: string) =>
  apiClient.get<CcjaPrecedentLight[]>(`/api/v1/ccja_precedents`, {
    params: typeIncident ? { type_incident: typeIncident } : undefined,
  });
